import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { API } from '../../api';
import { LearnerLessonVideo as LearnerVideo } from './types';
import { vb } from './theme';

// Learner-side lesson video block (prototype screen 8): the published video
// with the AI disclosure. Renders nothing when the lesson has no video.
const LearnerLessonVideo: React.FC<{
  programId: string;
  moduleId: string;
  lessonId: string;
  enabled?: boolean;
  // Lets the page wrap the block (e.g. in an Elliott message) only when a video exists.
  wrap?: (node: React.ReactElement) => React.ReactElement;
}> = ({ programId, moduleId, lessonId, enabled = true, wrap }) => {
  const [video, setVideo] = useState<LearnerVideo | null>(null);
  useEffect(() => {
    if (!enabled || !programId || !moduleId || !lessonId) return undefined;
    let alive = true;
    API.getLearnerLessonVideo({ programId, moduleId, lessonId })
      .then((res: any) => alive && setVideo(res?.data?.video ?? null))
      .catch(() => alive && setVideo(null));
    return () => {
      alive = false;
    };
  }, [enabled, programId, moduleId, lessonId]);

  if (!video) return null;
  const block = (
    <Box data-testid="learner-lesson-video" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box
        sx={{
          position: 'relative',
          borderRadius: '12px',
          overflow: 'hidden',
          border: `1px solid ${vb.border}`,
          background: '#000'
        }}
      >
        <Box
          component="video"
          controls
          preload="metadata"
          poster={video.thumbnailUrl ?? undefined}
          src={video.videoUrl}
          aria-label="Lesson video narrated by Elliott"
          sx={{ display: 'block', width: '100%', aspectRatio: '16 / 9' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1,
            py: 0.4,
            borderRadius: '6px',
            background: 'rgba(0,0,0,0.6)',
            color: vb.text,
            fontFamily: vb.font,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em'
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 13, color: vb.brand }} />
          Artificially generated
        </Box>
      </Box>
      <Typography sx={{ fontFamily: vb.font, fontSize: 12.5, color: vb.textTertiary }}>
        <Box component="span" sx={{ color: vb.textSecondary, fontWeight: 600 }}>
          This video was created with AI, narrated by Elliott.
        </Box>{' '}
        Content and imagery are artificially generated.
      </Typography>
    </Box>
  );
  return wrap ? wrap(block) : block;
};

export default LearnerLessonVideo;
